/*#
# Showcase: what a finished task has to show

A closed task can carry a **showcase** (owner, 2026-09-28; board #2439): a
picture or a video of what shipped, a title, a short description and a link.
The board shows the recent ones in a carousel above the scoreboard's tiles,
and only there: on a board the lanes need the space (owner, 2026-10-01).

- **title**: defaults to the task's title.
- **text**: required, at most 200 characters. Markdown **bold** is the only
  formatting; everything else shows as written.
- **media**: required: a picture (or a video) URL, http(s). A showcase is
  something to see (owner, 2026-09-30). Uploads come with blob storage.
- **alt**: what the picture shows; "screen capture of <title>" unless given.
- **link**: optional: the docs, a demo page, a video about it.

A showcase can be added when the task closes or any time after, by a person
or an agent (`virta showcase #n "text" --media … --link …`, or the MCP tool).
Showcasing again replaces it, and `--withdraw` takes it down. The carousel
orders by when a task was showcased, not when it closed.

**What the carousel shows**: the `n` most recent showcases (12 by default: it divides evenly into one, two, three or four at a time),
then the latest one from each other project that has one, so every project
with something to show is there. No showcases, no carousel. (`project`
picks one project's `n` most recent, for a page that shows just one.)
It shows several at once: half as many as the tiles below it have columns
(four columns of tiles, two showcases side by side), and never fewer than
one.

```js
import { pickShowcases } from 'tosijs-virta'

const shown = (num, project, at) => ({
  num, tags: [`project:${project}`], closedAt: at,
  showcase: { title: `#${num}`, text: 'x', media: 'https://x.dev/a.png', alt: '', link: '', at, by: 'me' },
})
const tasks = [
  shown(1, 'ui', '2026-09-01'), shown(2, 'ui', '2026-09-02'),
  shown(3, '3d', '2026-08-01'),
]
pickShowcases(tasks, { n: 1 }).map((t) => t.num)       // [2, 3]
pickShowcases(tasks, { n: 5, project: 'ui' }).map((t) => t.num)  // [2, 1]
```
*/

/*{ "parent": "Reference", "order": 35 }*/

import type { AttachedFile, Showcase, Task } from './model'
import { tagValues } from './tags'

/** The longest a showcase's description may be. */
export const SHOWCASE_TEXT_MAX = 200

/** How many showcases a carousel shows side by side over `columns` columns of tiles: half, at least one (owner, 2026-10-13). */
export const showcasesAcross = (columns: number): number =>
  Math.max(1, Math.floor((Number.isFinite(columns) ? columns : 1) / 2))

/** How many recent showcases a carousel shows, before one per other project. */
export const SHOWCASE_COUNT = 12

export interface ShowcaseInput {
  title?: string
  text: string
  media?: string
  /** a stored picture instead of a media URL (board #2436) */
  file?: AttachedFile
  alt?: string
  link?: string
}

const httpUrl = (url: string) => /^https?:\/\/\S+$/i.test(url)

/**
 * A showcase as it will be written, or why it can't be: the reason names
 * the fix, so an agent can act on it.
 */
export function checkShowcase(
  input: ShowcaseInput,
  taskTitle: string
):
  | {
      ok: true
      title: string
      text: string
      media: string
      file?: AttachedFile
      alt: string
      link: string
    }
  | { ok: false; why: string } {
  const text = (input.text ?? '').trim()
  if (!text) return { ok: false, why: 'a showcase needs a description (text)' }
  if (text.length > SHOWCASE_TEXT_MAX)
    return {
      ok: false,
      why: `the description is ${text.length} characters; a showcase allows ${SHOWCASE_TEXT_MAX}`,
    }
  const media = (input.media ?? '').trim()
  const link = (input.link ?? '').trim()
  if (!media && !input.file)
    return {
      ok: false,
      why: 'a showcase needs a picture: media, the http(s) URL of an image (or a video)',
    }
  if (media && !httpUrl(media))
    return {
      ok: false,
      why: 'media must be an http(s) URL of an image or video',
    }
  if (link && !httpUrl(link))
    return { ok: false, why: 'link must be an http(s) URL' }
  const title = (input.title ?? '').trim() || taskTitle
  return {
    ok: true,
    title,
    text,
    media,
    ...(input.file ? { file: input.file } : {}),
    alt: (input.alt ?? '').trim() || `screen capture of ${title}`,
    link,
  }
}

/**
 * What a carousel shows, newest first: the `n` most recent showcases, then
 * the latest from each project not already among them. With `project`,
 * only that project's `n` most recent.
 */
export function pickShowcases<
  T extends {
    tags: Task['tags']
    showcase: (Pick<Showcase, 'media' | 'at'> & { file?: unknown }) | null
  }
>(tasks: readonly T[], options: { n?: number; project?: string } = {}): T[] {
  const n = options.n ?? SHOWCASE_COUNT
  const projectOf = (t: T) => tagValues(t.tags, 'project')[0] ?? ''
  // only what has a picture: one made before pictures were required waits
  const all = tasks
    .filter((t) => t.showcase?.media || t.showcase?.file)
    .filter((t) => !options.project || projectOf(t) === options.project)
    .sort((a, b) =>
      a.showcase!.at < b.showcase!.at
        ? 1
        : a.showcase!.at > b.showcase!.at
        ? -1
        : 0
    )
  const recent = all.slice(0, n)
  if (options.project) return recent
  const shown = new Set(recent.map(projectOf))
  const more: T[] = []
  for (const t of all.slice(n)) {
    const p = projectOf(t)
    if (!shown.has(p)) {
      shown.add(p)
      more.push(t)
    }
  }
  return [...recent, ...more]
}
